import React, { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion, useInView } from 'framer-motion';
import { PlayIcon } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { EASE_OUT } from '../../utils/motion';
import { Reveal } from '../ui/Reveal';
export function Process() {
  const {
    t
  } = useLanguage();
  const stages = t.process.stages;
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, {
    margin: '-120px'
  });
  const [active, setActive] = useState(0);
  useEffect(() => {
    if (!inView) return;
    const id = window.setInterval(() => {
      setActive(a => (a + 1) % stages.length);
    }, 1000);
    return () => window.clearInterval(id);
  }, [inView, stages.length]);
  const progress = active / (stages.length - 1);
  const dotClass = (i: number) => i < active ? 'border-cyan/50 bg-cyan/50' : i === active ? 'scale-125 border-cyan bg-cyan' : 'border-white/25 bg-ink';
  const labelClass = (i: number) => i === active ? 'text-white' : i < active ? 'text-white/60' : 'text-white/30';
  return <section className="overflow-hidden bg-ink py-28 md:py-40" data-id="element-190" data-mp="2rqda28eku0sa">
      <div className="mx-auto max-w-6xl px-5 md:px-6" data-id="element-191" data-mp="2rok0olckx2gl">
        <Reveal data-id="element-192" data-mp="2wiwlaypskjtx">
          <h2 className="mx-auto max-w-4xl text-center text-4xl font-semibold leading-[1.05] tracking-[-0.035em] text-white md:text-6xl" data-id="element-193" data-mp="2wiere8qlqhp6">
            {t.process.title}
          </h2>
        </Reveal>

        <div ref={ref} className="mt-20 md:mt-28" data-id="element-194" data-mp="23usdo7nntwol">
          {/* Desktop: horizontal flow */}
          <div className="relative hidden grid-cols-5 md:grid" data-id="element-195" data-mp="28skczmqmwl2t">
            <div className="absolute left-[10%] right-[10%] top-[7px] h-px bg-white/10" data-id="element-196" data-mp="2ctcokmmzb229">
              <motion.div className="h-full origin-left bg-cyan" animate={{
              scaleX: progress
            }} transition={{
              duration: 0.22,
              ease: EASE_OUT
            }} data-id="element-197" data-mp="2ht8jlwdp7j5o" />
            </div>
            {stages.map((stage, i) => <div key={stage} className="relative flex flex-col items-center gap-5" data-id="element-198" data-mp="3t9p25ykjufwr" data-mpi={i}>
                <span className={`h-[15px] w-[15px] rounded-full border transition-[transform,background-color,border-color] duration-200 ease-out ${dotClass(i)}`} data-id="element-199" data-mp="1t7piy0qhgokk" />
                <span className={`whitespace-nowrap text-xs font-medium tracking-[0.16em] transition-colors duration-200 ${labelClass(i)}`} data-id="element-200" data-mp="231pvgno3gw3b">
                  {stage}
                </span>
              </div>)}
          </div>

          {/* Mobile: vertical flow */}
          <div className="relative mx-auto max-w-xs pl-9 md:hidden" data-id="element-201" data-mp="15e7cyl4msfm4">
            <div className="absolute bottom-2 left-[7px] top-2 w-px bg-white/10" data-id="element-202" data-mp="1k6j2frnu3drb">
              <motion.div className="w-full origin-top bg-cyan" style={{
              height: '100%'
            }} animate={{
              scaleY: progress
            }} transition={{
              duration: 0.22,
              ease: EASE_OUT
            }} data-id="element-203" data-mp="1fdg3t5479e49" />
            </div>
            <ol className="space-y-8" data-id="element-204" data-mp="2yce05ppymbu4">
              {stages.map((stage, i) => <li key={stage} className="relative flex items-center" data-id="element-205" data-mp="333mb4q6xu59e" data-mpi={i}>
                  <span className={`absolute -left-9 h-[15px] w-[15px] rounded-full border transition-[transform,background-color,border-color] duration-200 ease-out ${dotClass(i)}`} data-id="element-206" data-mp="334xguvjy1n35" />
                  <span className={`text-sm font-medium tracking-[0.14em] transition-colors duration-200 ${labelClass(i)}`} data-id="element-207" data-mp="3h3duz7k4yfvb">
                    {stage}
                  </span>
                </li>)}
            </ol>
          </div>

          <div className="mx-auto mt-16 flex max-w-md items-center gap-4 rounded-2xl border border-white/10 bg-surface p-4 md:mt-24" data-id="element-208" data-mp="384tlmx0gtijr">
            <div className="grid h-14 w-14 shrink-0 place-items-center rounded-xl bg-surface2" data-id="element-209" data-mp="1wn0s1fxxh6ez">
              <PlayIcon className="h-5 w-5 fill-white/80 text-white/80" aria-hidden="true" data-id="element-210" data-mp="21n6dgedltwom" />
            </div>
            <div className="min-w-0" data-id="element-211" data-mp="2gwjsp30kfih2">
              <div className="truncate text-sm font-medium text-white" data-id="element-212" data-mp="2gxl7nikpcp42">{t.process.card}</div>
              <div className="mt-0.5 text-xs text-white/45" data-id="element-213" data-mp="2c40dy2q56hao">{t.process.updated}</div>
            </div>
            <div className="ml-auto" aria-live="polite" data-id="element-214" data-mp="276qjg8nwbffz">
              <AnimatePresence mode="wait" initial={false} data-id="element-215" data-mp="235ohhk2lgpab">
                <motion.span key={stages[active]} initial={{
                opacity: 0,
                y: 6
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -6
              }} transition={{
                duration: 0.14,
                ease: EASE_OUT
              }} className="block whitespace-nowrap rounded-full bg-cyan/10 px-2.5 py-1 text-[10px] font-medium tracking-[0.12em] text-cyan" data-id="element-216" data-mp="23329xkfpe9s3">
                  {stages[active]}
                </motion.span>
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>
    </section>;
}